import { chargeableDays } from './absenceDays';
import type {
  ObjectAggregate,
  CustomerAggregate,
  EmployeeAggregate,
} from '../types/lists';
import type {
  PlanningEntry,
  PlanningTask,
  AbsenceListDto,
} from '../types/dashboard';
import type {
  FacilityFlowCoreModulesCustomersDtosObjectDto,
  FacilityFlowCoreModulesCustomersDtosCustomerListDto,
  FacilityFlowCoreModulesEmployeesDtosEmployeeListDto,
} from '../api/generated/models';
import type { TaskListDto } from '../types';
import { timeToMin } from './dashboardData';

export type CustomerListItem = FacilityFlowCoreModulesCustomersDtosCustomerListDto;
export type ObjectListItem = FacilityFlowCoreModulesCustomersDtosObjectDto;
export type EmployeeListItem = FacilityFlowCoreModulesEmployeesDtosEmployeeListDto;

function durationOfTask(t: PlanningTask): number {
  if (typeof t.durationMinutes === 'number' && t.durationMinutes > 0) return t.durationMinutes;
  return Math.max(0, timeToMin(t.endTime) - timeToMin(t.startTime));
}

function uniqueCount<T>(arr: T[]): number {
  return new Set(arr).size;
}

/**
 * Aggregate per-object planning signals for the active week. `scheduled` comes from the
 * backend (FAFD-56): minutes due according to the task schedule rules and the planned minutes
 * of exactly those tasks. "Eingeplant" uses only these two, so ad-hoc work, skipped tasks or
 * absence days cannot push it up; `weeklyHours` still shows everything planned at the object.
 */
export function aggregateObjectWeek(
  objectId: string,
  planningEntries: PlanningEntry[],
  tasksByObject: Map<string, TaskListDto[]>,
  scheduled: { due: number; planned: number } = { due: 0, planned: 0 },
): ObjectAggregate {
  const employeesSet = new Set<string>();
  const serviceSet = new Set<string>();
  let weeklyMinutes = 0;
  let planUntil: string | null = null;

  for (const entry of planningEntries) {
    if (!entry.employeeId || !entry.date) continue;
    const matching = (entry.tasks ?? []).filter((t) => t.objectId === objectId);
    if (matching.length === 0) continue;

    employeesSet.add(entry.employeeId);
    for (const task of matching) {
      weeklyMinutes += durationOfTask(task);
      if (task.serviceId) serviceSet.add(task.serviceId);
    }
    if (!planUntil || entry.date > planUntil) planUntil = entry.date;
  }

  const tasksForObject = tasksByObject.get(objectId) ?? [];
  const activeTasks = tasksForObject.filter((t) => t.isActive !== false).length;
  const weeklyHours = weeklyMinutes / 60;
  const dueHours = scheduled.due / 60;
  const scheduledPlannedHours = scheduled.planned / 60;
  const planned = dueHours > 0 ? scheduledPlannedHours / dueHours : null;
  const hasPlan = weeklyMinutes > 0 || activeTasks > 0;

  return {
    objectId,
    activeTasks,
    weeklyHours,
    dueHours,
    scheduledPlannedHours,
    planned,
    employeeCount: employeesSet.size,
    serviceIds: Array.from(serviceSet),
    planUntilIso: planUntil,
    hasPlan,
  };
}

/**
 * "Ø Eingeplant" of the objects KPI strip: planned ÷ due hours over the objects with due work,
 * as a rounded percentage; null when nothing is due anywhere (FAFD-56).
 */
export function summarizeObjectsPlanned(aggregates: Iterable<ObjectAggregate>): number | null {
  let planned = 0;
  let due = 0;
  for (const agg of aggregates) {
    if (agg.dueHours <= 0) continue;
    planned += agg.scheduledPlannedHours;
    due += agg.dueHours;
  }
  return due > 0 ? Math.round((planned / due) * 100) : null;
}

/**
 * Aggregate per-customer signals: object count + weekly hours summed across their objects.
 */
export function aggregateCustomerWeek(
  customer: CustomerListItem,
  objects: ObjectListItem[],
  planningEntries: PlanningEntry[],
): CustomerAggregate {
  const objectIds = new Set(
    objects.filter((o) => o.customerId === customer.id && o.id).map((o) => o.id!),
  );

  let weeklyMinutes = 0;
  for (const entry of planningEntries) {
    for (const task of entry.tasks ?? []) {
      if (task.objectId && objectIds.has(task.objectId)) {
        weeklyMinutes += durationOfTask(task);
      }
    }
  }

  return {
    customerId: customer.id ?? '',
    objectCount: objectIds.size,
    weeklyHours: weeklyMinutes / 60,
    isNew: false,
  };
}

/**
 * Aggregate per-employee weekly + monthly stats.
 * `weekEntries` should contain this ISO week, `monthEntries` the running month.
 */
export function aggregateEmployeeWeek(
  employeeId: string,
  weekEntries: PlanningEntry[],
  monthEntries: PlanningEntry[],
  monthAbsences: AbsenceListDto[],
): EmployeeAggregate {
  let weekMinutes = 0;
  let weekTargetHours = 0;
  for (const entry of weekEntries) {
    if (entry.employeeId !== employeeId) continue;
    for (const task of entry.tasks ?? []) {
      weekMinutes += durationOfTask(task);
    }
    weekTargetHours += entry.targetHours ?? 0;
  }

  let monthMinutes = 0;
  let monthTargetHours = 0;
  for (const entry of monthEntries) {
    if (entry.employeeId !== employeeId) continue;
    for (const task of entry.tasks ?? []) {
      monthMinutes += durationOfTask(task);
    }
    monthTargetHours += entry.targetHours ?? 0;
  }

  const weeklyHours = weekMinutes / 60;
  const monthHours = monthMinutes / 60;
  const utilization = weekTargetHours > 0 ? weeklyHours / weekTargetHours : 0;

  let absenceDays = 0;
  for (const a of monthAbsences) {
    if (a.employeeId !== employeeId) continue;
    if (a.approvalStatus !== 'Approved') continue;
    absenceDays += chargeableDays(a);
  }

  return {
    employeeId,
    weeklyHours,
    targetHours: weekTargetHours,
    monthHours,
    monthTarget: monthTargetHours,
    utilization,
    absenceDays,
  };
}

/**
 * Weekly totals for the employees KPI strip: target hours of everyone, and planned ÷ target
 * hours over the employees with target hours, as a rounded percentage.
 */
export function summarizeEmployeeWeek(
  employees: EmployeeListItem[],
  weekEntries: PlanningEntry[],
  monthEntries: PlanningEntry[],
  monthAbsences: AbsenceListDto[],
): { targetHours: number; avgUtilization: number } {
  let targetHours = 0;
  let plannedWithTarget = 0;
  for (const emp of employees) {
    if (!emp.id) continue;
    const agg = aggregateEmployeeWeek(emp.id, weekEntries, monthEntries, monthAbsences);
    targetHours += agg.targetHours;
    if (agg.targetHours > 0) plannedWithTarget += agg.weeklyHours;
  }
  const avgUtilization = targetHours > 0 ? Math.round((plannedWithTarget / targetHours) * 100) : 0;
  return { targetHours, avgUtilization };
}

export function getMonthRange(date: Date = new Date()): { start: string; end: string } {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: fmt(start), end: fmt(end) };
}

export function uniqueServicesFromTasks(tasks: TaskListDto[] | null | undefined): string[] {
  if (!tasks) return [];
  return Array.from(new Set(tasks.map((t) => t.serviceName).filter(Boolean) as string[]));
}

export { uniqueCount };

export type ObjectSortId = 'name' | 'planned' | 'hours';

export const OBJECT_SORT_IDS: readonly ObjectSortId[] = ['name', 'planned', 'hours'];

const nameCollator = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });

/**
 * Sorts objects by the chosen key, then by name, then by id — so equal entries keep
 * the same order across reloads regardless of what order the server returned them in.
 */
export function sortObjectList<T extends Pick<ObjectListItem, 'id' | 'name'>>(
  objects: readonly T[],
  sortBy: ObjectSortId,
  aggregates: Map<string, Pick<ObjectAggregate, 'planned' | 'weeklyHours'>>,
): T[] {
  const metric = (o: T): number => {
    const agg = aggregates.get(o.id ?? '');
    // Objects without due work (null) sort last.
    if (sortBy === 'planned') return agg?.planned ?? -1;
    if (sortBy === 'hours') return agg?.weeklyHours ?? 0;
    return 0;
  };
  return [...objects].sort(
    (a, b) =>
      metric(b) - metric(a) ||
      nameCollator.compare(a.name ?? '', b.name ?? '') ||
      (a.id ?? '').localeCompare(b.id ?? ''),
  );
}
